import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';

function SiteInvoicesTab({ siteId, siteName, customerId, customerName }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sorting
  const { items: sortedInvoices, requestSort, getSortIndicator } = useSortableTable(invoices, { key: 'invoice_date', direction: 'desc' });

  useEffect(() => {
    fetchInvoices();
  }, [siteId]);

  const fetchInvoices = async () => {
    try {
      const res = await apiFetch('/api/invoices');
      const allInvoices = await res.json();
      
      // Filter invoices for this specific site
      const siteInvoices = allInvoices.filter(invoice => invoice.site_id === parseInt(siteId));
      setInvoices(siteInvoices);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Invoices for This Site</h2>
          <p className="text-gray-600 mt-1">
            {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'} for {siteName}
          </p>
        </div>
        <button
          onClick={() => navigate(`/finance/invoices/new?customer_id=${customerId}&site_id=${siteId}`)}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
        >
          ➕ Create Invoice
        </button>
      </div>

      {/* Invoices List */}
      {invoices.length === 0 ? (
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="text-6xl mb-4">💰</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Invoices for This Site</h3>
          <p className="text-gray-600 mb-4">
            Create the first invoice for {siteName}
          </p>
          <button
            onClick={() => navigate(`/finance/invoices/new?customer_id=${customerId}&site_id=${siteId}`)}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
          >
            ➕ Create First Invoice
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-300 border-b border-gray-400">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('invoice_ref')}>
                  Invoice #{getSortIndicator('invoice_ref')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('invoice_date')}>
                  Date{getSortIndicator('invoice_date')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('due_date')}>
                  Due Date{getSortIndicator('due_date')}
                </th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('status')}>
                  Status{getSortIndicator('status')}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('total')}>
                  Total{getSortIndicator('total')}
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Amount Paid
                </th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedInvoices.map((invoice, index) => (
                <tr 
                  key={invoice.id}
                  className={`hover:bg-blue-50 cursor-pointer transition ${index % 2 === 1 ? 'bg-blue-50' : 'bg-white'}`}
                  onClick={() => navigate(`/finance/invoices/${invoice.id}`)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {invoice.invoice_ref || `INV${invoice.id}`}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDate(invoice.invoice_date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDate(invoice.due_date)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {invoice.status || 'draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                    {formatCurrency(invoice.total)}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-gray-700">
                    {formatCurrency(invoice.amount_paid || 0)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/finance/invoices/${invoice.id}`);
                      }}
                      className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black rounded text-xs font-medium transition"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SiteInvoicesTab;
